import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { api } from "@/lib/api-client";
import { useAppStore } from "@/store/app-store";
import { Button } from "@/components/ui/Button";
import { PasswordInput } from "@/components/ui/PasswordInput";

// Someone added to a property gets a temporary password; before anything else they set their own.
export function FirstLoginPasswordPage() {
  const user = useAppStore((state) => state.user);
  const setUser = useAppStore((state) => state.setUser);
  const clearSession = useAppStore((state) => state.clearSession);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const change = useMutation({
    mutationFn: () => api<{ message: string }>("/auth/change-password", { method: "PATCH", body: { currentPassword, newPassword } }),
    onSuccess: () => { if (user) setUser({ ...user, mustResetPassword: false }); },
  });
  const mismatch = repeat !== "" && repeat !== newPassword;
  const valid = currentPassword !== "" && newPassword.length >= 8 && newPassword === repeat && newPassword !== currentPassword;

  return (
    <main className="first-login">
      <section className="card first-login__card">
        <span className="team-row__icon"><KeyRound size={18} aria-hidden="true" /></span>
        <h1>Set your own password</h1>
        <p>Welcome{user?.name ? `, ${user.name}` : ""}. You signed in with a temporary password - choose your own to continue.</p>
        <form className="first-login__form" noValidate onSubmit={(event) => { event.preventDefault(); if (valid) change.mutate(); }}>
          <div className="field">
            <label htmlFor="first-login-current">Temporary password</label>
            <PasswordInput id="first-login-current" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="first-login-new">New password</label>
            <PasswordInput id="first-login-new" autoComplete="new-password" aria-describedby="first-login-new-hint" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} />
            <small className="field-hint" id="first-login-new-hint">At least 8 characters.</small>
          </div>
          <div className="field">
            <label htmlFor="first-login-repeat">Repeat new password</label>
            <PasswordInput id="first-login-repeat" autoComplete="new-password" aria-invalid={mismatch} value={repeat} onChange={(event) => setRepeat(event.target.value)} />
          </div>
          {mismatch && <div className="form-error">The two passwords don't match.</div>}
          {change.error && <div className="form-error">{change.error.message}</div>}
          <div className="first-login__actions">
            <Button type="submit" loading={change.isPending} disabled={!valid}>Save and continue</Button>
            <Button type="button" variant="ghost" onClick={clearSession}>Log out</Button>
          </div>
        </form>
      </section>
    </main>
  );
}
